import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/* Visible stops on the Time Machine slider. The dial tells a story in three
   acts: the web as it actually was (1986–2026), its lowest point (2036, the
   machine-first dystopia), and what comes after — a hopeful, human web
   (2046 Brasil) and the far future (2126). Every stop carries a year. */
export const ERAS = [
  { year: 1986, label: '1986 BBS', color: 'var(--accent-leaf)' },
  { year: 1996, label: '1996 WEB1', color: 'var(--accent-sun)' },
  { year: 2006, label: '2006 WEB2', color: 'var(--accent-azul)' },
  { year: 2016, label: '2016 FLAT', color: 'var(--primary-hover)' },
  { year: 2020, label: '2020 SAAS', color: 'var(--accent-mint)' },
  { year: '2026trend', label: '2026 SLOP', color: 'var(--accent-acid)' },
  { year: 2036, label: '2036 MX', color: 'var(--accent-signal)' },
  { year: 'brasil', label: '2046 BRASIL', color: 'var(--accent-terracotta)' },
  { year: 'future', label: '2126 CAMPO', color: 'var(--accent-future)' },
];

/* Reachable, but deliberately not on the slider and not behind any route —
   no URL ever points here, so there's nothing for a person or a crawler to
   find by guessing or scanning links. The only way in is a hidden in-page
   trigger that calls setEraByYear directly:
   - commons: the other utopia of 2046 — six quick clicks on "rev 2036.N" in 2036.
   - superapp: type "superapp" (or "miniapp") in the 2020 era's ⌘K palette. */
export const HIDDEN_ERAS = [
  { year: 'commons', label: '2046 COMMONS', color: 'var(--accent-commons)' },
  { year: 'superapp', label: 'MINIAPP', color: 'var(--accent-app)' },
];

const ALL_ERAS = [...ERAS, ...HIDDEN_ERAS];

const EraContext = createContext(null);

export const EraProvider = ({ children }) => {
  const [isOn, setIsOn] = useState(false);
  const [index, setIndex] = useState(0);
  const [overrideYear, setOverrideYear] = useState(null);

  const era = isOn
    ? (overrideYear != null ? ALL_ERAS.find((e) => e.year === overrideYear) : ERAS[index])
    : null;

  useEffect(() => {
    ALL_ERAS.forEach((e) => document.body.classList.remove(`theme-${e.year}`));
    if (isOn && era) {
      document.body.classList.add(`theme-${era.year}`);
    }
    return () => {
      ALL_ERAS.forEach((e) => document.body.classList.remove(`theme-${e.year}`));
    };
  }, [isOn, era]);

  // Used by the slider: jump to a visible stop, dropping any hidden override.
  const selectIndex = useCallback((i) => {
    setOverrideYear(null);
    setIndex(i);
  }, []);

  // Used by easter eggs: jump to any era by year, visible or hidden.
  const setEraByYear = useCallback((year) => {
    const visibleIdx = ERAS.findIndex((e) => e.year === year);
    if (visibleIdx !== -1) {
      setOverrideYear(null);
      setIndex(visibleIdx);
    } else {
      setOverrideYear(year);
    }
    setIsOn(true);
  }, []);

  const value = {
    isOn,
    setIsOn,
    index,
    setIndex: selectIndex,
    setEraByYear,
    era,
  };

  return <EraContext.Provider value={value}>{children}</EraContext.Provider>;
};

export const useEra = () => useContext(EraContext);
